import React, { useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@klear/ui/components/dialog'
import { Button } from '@klear/ui/components/button'
import FormBasisPointsInput from '@klear/ui/form/basis-points-input'
import FormCombobox from '@klear/ui/form/combobox'
import { RATING_OPTIONS, RATING_SCALE_OPTIONS } from '@klear/invoice-offer-fee-rate/options'
import type { RouterOutputs } from '@klear/ui/lib/trpc'
import {
  EMPTY_INVOICE_OFFER_FEE_RATE_FORM,
  invoiceOfferFeeRateFormToCreateWire,
  invoiceOfferFeeRateFormToUpdateWire,
  invoiceOfferFeeRateToForm,
  type InvoiceOfferFeeRateForm
} from '@klear/invoice-offer-fee-rate/wire.client'
import { useInvoiceOfferFeeRateMutations } from '@klear/ui/hooks/use-funder-pricing'
import { SUBSCRIPTION_TIER_NAMES } from '@klear/constants/subscription-tier'

type FeeRateRow = RouterOutputs['funderPricing']['listInvoiceOfferFeeRates'][number]

interface PricingCardDialogProps {
  isOpen: boolean
  onClose: () => void
  pricingCard: FeeRateRow | null
}

const PricingCardDialog: React.FC<PricingCardDialogProps> = ({ isOpen, onClose, pricingCard }) => {
  const isEditing = !!pricingCard
  const { createInvoiceOfferFeeRate, updateInvoiceOfferFeeRate, isCreating, isUpdating } =
    useInvoiceOfferFeeRateMutations()

  const form = useForm<InvoiceOfferFeeRateForm>({
    defaultValues: EMPTY_INVOICE_OFFER_FEE_RATE_FORM
  })

  useEffect(() => {
    if (isOpen) {
      if (pricingCard) {
        form.reset(invoiceOfferFeeRateToForm(pricingCard))
      } else {
        form.reset(EMPTY_INVOICE_OFFER_FEE_RATE_FORM)
      }
    }
  }, [isOpen, pricingCard, form])

  const ratingScale = form.watch('ratingScale')

  const tierOptions = SUBSCRIPTION_TIER_NAMES.map((tier) => ({ value: tier, label: tier }))

  const handleSubmit = async (data: InvoiceOfferFeeRateForm) => {
    try {
      if (isEditing && pricingCard?.id != null) {
        await updateInvoiceOfferFeeRate(invoiceOfferFeeRateFormToUpdateWire(data, pricingCard.id))
      } else {
        await createInvoiceOfferFeeRate(invoiceOfferFeeRateFormToCreateWire(data))
      }
      onClose()
    } catch (error) {
      console.error('Failed to save invoice offer fee rate:', error)
    }
  }

  const isLoading = isCreating || isUpdating

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit' : 'Add'} Invoice Offer Fee Rate</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the annualized rate for this invoice offer fee rate.'
              : 'Create a new invoice offer fee rate entry for a rating/tier combination.'}
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormCombobox
              label="Rating Scale"
              name="ratingScale"
              options={RATING_SCALE_OPTIONS}
              disabled={isEditing}
              onChange={() => {
                form.setValue('rating', '')
              }}
            />

            <FormCombobox
              label="Rating"
              name="rating"
              options={RATING_OPTIONS[ratingScale] ?? RATING_OPTIONS.IG}
              disabled={isEditing}
              placeholder={`Select ${ratingScale === 'IG' ? 'IG' : 'Allianz'} rating`}
              valueType="string"
            />

            <FormCombobox
              label="Subscription Tier"
              name="subscriptionTierName"
              options={tierOptions}
              disabled={isEditing}
            />

            <FormBasisPointsInput label="Annualized Rate" name="annualizedRate" />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export default PricingCardDialog
